import {Injectable} from '@angular/core';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Cart, OrderEntry} from '@spartacus/cart/base/root';
import {EventService} from '@spartacus/core';
import {DcpEntryGroup} from '@dcp/configurator';
import {DcpActiveCartService} from '../../dcp-active-cart.service';
import {DcpDataService} from '../dcp-data.service';
import {DcpEddlCartStateService} from './dcp-eddl-cart-state.service';
import {EddlCartEventBase} from './events/eddl-cart-event-base';
import {EddlAdobeTrackingData} from './models/eddl-adobe-tracking-data';

/**
 * Abstract base class for EDDL cart event services.
 * Consolidates common logic for add-to-cart and remove-from-cart services.
 *
 * Child services must implement:
 * - getEventName(): Returns the specific event type ('cartAdd', 'cartRemove')
 * - getData(): Event-specific cart retrieval and data processing logic
 */
@UntilDestroy()
@Injectable()
export abstract class AbstractEddlCartService<
  T extends EddlCartEventBase,
> extends DcpDataService<EddlAdobeTrackingData> {
  constructor(
    protected eventService: EventService,
    protected eddlCartStateService: DcpEddlCartStateService,
    protected activeCartService: DcpActiveCartService,
  ) {
    super();
  }

  /**
   * Returns the event name for Adobe tracking.
   * Must be implemented by child services.
   */
  protected abstract getEventName(): string;

  /**
   * Initializes the tracking data structure with cart information.
   * Creates the nested ecommerce object with cart ID, currency, and empty products array.
   *
   * @param cart - The cart to extract data from
   * @returns Initialized EddlAdobeTrackingData object
   */
  protected initializeTrackingData(cart: Cart): EddlAdobeTrackingData {
    return {
      event: this.getEventName(),
      ecommerce: {
        cart: {
          id: cart.guid,
          currency: cart.totalPrice?.currencyIso,
        },
        products: [],
      },
    };
  }

  /**
   * Populates the products array in the tracking data.
   * Transforms each OrderEntry into EddlAdobeTrackingDataProductData using the cart state service.
   *
   * @param entries - Array of order entries to process
   * @param data - Tracking data object to populate
   */
  protected populateProducts(entries: OrderEntry[], data: EddlAdobeTrackingData): void {
    entries.forEach((entry) => {
      const productData = this.eddlCartStateService.getProductTrackingData(entry);
      if (productData) {
        data.ecommerce.products.push(productData);
      }
    });
  }

  /**
   * Recursively collects all entries from entry groups including nested groups.
   * This handles both main products and their add-ons in the hierarchy.
   *
   * @param entryGroups - Array of entry groups to process
   * @returns Flattened array of all OrderEntry objects, deduplicated by entryNumber
   * (an entry can belong to multiple groups via entryGroupNumbers; duplicates would
   * make Adobe double-count the line item - DCP-61177)
   */
  protected getAllEntriesFromGroups(entryGroups: DcpEntryGroup[]): OrderEntry[] {
    if (!entryGroups?.length) {
      return [];
    }
    const allEntries = entryGroups.flatMap((entryGroup) => [
      ...(entryGroup.entries ?? []),
      ...this.getAllEntriesFromGroups(entryGroup.entryGroups),
    ]);
    const seenEntryNumbers = new Set<number>();
    return allEntries.filter((entry) => {
      const entryNumber = entry?.entryNumber;
      if (entryNumber == null) {
        return true;
      }
      if (seenEntryNumbers.has(entryNumber)) {
        return false;
      }
      seenEntryNumbers.add(entryNumber);
      return true;
    });
  }

  /**
   * Collects entries for a given event from the cart's entry groups.
   * Handles both CPS and Legacy product types with appropriate fallbacks.
   *
   * Logic:
   * 1. If event has productCode (CPS): Get CPS groups and extract all entries
   * 2. If event has entry with product code (Legacy): Get Legacy groups and extract entries
   * 3. Fallback: Return single entry from event if available
   *
   * @param event - The cart event (add/edit/remove)
   * @param cart - The cart containing entry groups
   * @returns Array of OrderEntry objects to track
   */
  protected collectEntriesForEvent(event: T, cart: Cart): OrderEntry[] {
    if (event.productCode) {
      // CPS Product case (includes SBN flow)
      const groups = this.eddlCartStateService.getRelevantCpsGroups(cart.entryGroups, event.productCode);
      const entries = this.getAllEntriesFromGroups(groups);
      if (entries.length === 0) {
        console.warn(`No entry groups found for CPS product code: ${event.productCode}`);
      }
      // Filter out configurator/template entries
      return this.eddlCartStateService.getRelevantCartEntries(entries);
    }

    // Legacy Product case
    const productCode = event.entry?.product?.code;
    if (productCode && cart.entryGroups) {
      const groups = this.eddlCartStateService.getRelevantLegacyGroups(cart.entryGroups, productCode);
      const entries = this.getAllEntriesFromGroups(groups);
      if (entries.length > 0) {
        // Filter out configurator/template entries
        return this.eddlCartStateService.getRelevantCartEntries(entries);
      }
    }

    // Fallback to single entry if no groups found
    return event.entry ? [event.entry] : [];
  }

  /**
   * Common fillData implementation for cart events.
   * Initializes the tracking data structure and populates it with product information.
   *
   * @param event - The cart event
   * @param entries - Array of entries to track
   * @param cart - The cart
   * @returns Populated EddlAdobeTrackingData object
   */
  fillData(event: T, entries: OrderEntry[], cart: Cart): EddlAdobeTrackingData {
    const data = this.initializeTrackingData(cart);
    this.populateProducts(entries, data);
    return data;
  }
}
